import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { BaseTabs, TabBar } from '@components';
import { BaseInput } from '@components/input';
import { LEARNING_TAB_BAR_SECTION_ID } from '@constants/anchors.ts';
import { LEARNING_PATH_TYPES } from '@constants/learningStates.ts';
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks.ts';
import { useLingui } from '@lingui/react/macro';
import { getFullUserInfo } from '@routes/___shared___/user.slice.ts';
import { DEFAULT_TAB_KEY, LEARNING_FILTER_KEYS } from '@routes/sidebar/learning/___shared___/constants.ts';
import {
    getFilterStateForTab,
    hasCareerRoadmapRank,
    useTabs,
    withLearningFilters,
} from '@routes/sidebar/learning/___shared___/helpers.tsx';
import type { LearningPathType, LearningState } from '@routes/sidebar/learning/api/learning.api.ts';
import {
    DEFAULT_TAB_INDEX,
    LEARNING_DEFAULT_PAGE_NUMBER,
    LEARNING_DEFAULT_PAGE_SIZE,
    LEARNING_SEARCH_DEBOUNCE_MS,
} from '@routes/sidebar/learning/constants.ts';
import {
    clearLearnings,
    getCareerRoadmapLearningCards,
    getLibraryLearningCards,
    getMandatoryLearningCards,
    getRecommendedLearningCards,
    updateLearningCardsRequestData,
    updateLearningCardsSearchForAll,
} from '@routes/sidebar/learning/model/learning.slice.ts';
import { Dashboard } from '@routes/sidebar/learning/ui/dashboard-tab/dashboard';
import { fetchServerTime } from '@shared/model/serverTime.slice.ts';
import { stringListToFilterRecord } from '@utils/commonSearch.ts';
import { debounce } from 'lodash';

import { GlobalLearningSearchWrapper, LearningPageWrapper } from './styled.tsx';
import WelcomeSection from './ui/welcome-section';

const MyLearning = () => {
    const { t } = useLingui();
    const location = useLocation();

    const [selectedTabSection, setSelectedTabSection] = useState(0);
    const [globalSearchValue, setGlobalSearchValue] = useState('');
    const learnings = useAppSelector((state) => state.learning.learningCards);
    const requestData = useAppSelector((state) => state.learning.learningCardsRequestData);
    const isRecommendedLockedByTenant =
        useAppSelector((state) => state.clientDetails.clientDetails?.pathwayDetails.isRecomendedLocked) ?? false;
    const isLibraryLockedByTenant =
        useAppSelector((state) => state.clientDetails.clientDetails?.pathwayDetails.isLibraryLocked) ?? false;
    const isCareerRoadmapLockedByTenant =
        useAppSelector((state) => state.clientDetails.clientDetails?.pathwayDetails.isCareerRoadmapLocked) ?? false;
    const hasCareerRoadmap = useAppSelector((state) => hasCareerRoadmapRank(state.user.userInfo));

    const dispatch = useAppDispatch();

    const TAB_BAR_OPTIONS = [t`Learning`, t`Dashboard`];

    const updateSearchForAllLearningTypes = useMemo(
        () =>
            debounce((value: string) => {
                dispatch(
                    updateLearningCardsSearchForAll({
                        textSearch: value,
                        pageNumber: LEARNING_DEFAULT_PAGE_NUMBER,
                        pageSize: LEARNING_DEFAULT_PAGE_SIZE,
                    }),
                );
            }, LEARNING_SEARCH_DEBOUNCE_MS),
        [dispatch],
    );

    useEffect(
        () => () => {
            updateSearchForAllLearningTypes.cancel();
        },
        [updateSearchForAllLearningTypes],
    );

    const onGlobalSearchChange = (value: string) => {
        setGlobalSearchValue(value);
        updateSearchForAllLearningTypes(value);
    };

    const onClearGlobalSearch = () => {
        updateSearchForAllLearningTypes.cancel();
        setGlobalSearchValue('');

        dispatch(
            updateLearningCardsSearchForAll({
                textSearch: '',
                pageNumber: LEARNING_DEFAULT_PAGE_NUMBER,
                pageSize: LEARNING_DEFAULT_PAGE_SIZE,
            }),
        );
    };

    const onTabChange = (tabName: string) => {
        const filterState: LearningState[] = getFilterStateForTab(tabName);
        const filters = stringListToFilterRecord(filterState, LEARNING_FILTER_KEYS.LearningState);

        // Update all types
        for (const pathType of Object.values(LEARNING_PATH_TYPES)) {
            dispatch(
                updateLearningCardsRequestData({
                    learningPathType: pathType,
                    requestData: {
                        ...withLearningFilters(requestData[pathType], [LEARNING_FILTER_KEYS.LearningState], filters),
                        pageNumber: LEARNING_DEFAULT_PAGE_NUMBER,
                        pageSize: LEARNING_DEFAULT_PAGE_SIZE,
                    },
                }),
            );
        }
    };

    const onSectionTabClick = (index: number) => {
        if (index === DEFAULT_TAB_INDEX && selectedTabSection !== DEFAULT_TAB_INDEX) {
            onTabChange(DEFAULT_TAB_KEY);
        }
        setSelectedTabSection(index);
    };

    const learningSearch = (
        <GlobalLearningSearchWrapper>
            <BaseInput
                value={globalSearchValue}
                onChange={(event) => onGlobalSearchChange(event.target.value)}
                placeholder={t`Search by title`}
                onClear={onClearGlobalSearch}
                allowClear
                data-cy="learning-search-input"
            />
        </GlobalLearningSearchWrapper>
    );

    const tabs = useTabs(
        learnings.Mandatory.resultsDescription?.overdue,
        isRecommendedLockedByTenant,
        isLibraryLockedByTenant,
        isCareerRoadmapLockedByTenant,
        hasCareerRoadmap,
        learningSearch,
    );

    const TAB_BAR_ITEMS = [
        <BaseTabs key="learning" defaultActiveKey={DEFAULT_TAB_KEY} items={tabs} onChange={onTabChange} />,
        <Dashboard key="dashboard" />,
    ];

    useEffect(() => {
        void dispatch(getMandatoryLearningCards({ ...requestData.Mandatory }));
    }, [dispatch, requestData.Mandatory]);
    useEffect(() => {
        void dispatch(getRecommendedLearningCards({ ...requestData.Recommended }));
    }, [dispatch, requestData.Recommended]);
    useEffect(() => {
        void dispatch(getLibraryLearningCards({ ...requestData.Library }));
    }, [dispatch, requestData.Library]);
    useEffect(() => {
        if (!hasCareerRoadmap) return;

        void dispatch(getCareerRoadmapLearningCards({ ...requestData.CareerRoadmap }));
    }, [dispatch, hasCareerRoadmap, requestData.CareerRoadmap]);

    // /me is refreshed only every 20 minutes; reload it here so Career roadmap visibility uses the latest ranks
    // (an admin can change the promotion rank or the next planned rank at any time).
    useEffect(() => {
        void dispatch(getFullUserInfo());
    }, [dispatch]);

    useEffect(() => {
        return () => {
            for (const learningPathType of Object.values(LEARNING_PATH_TYPES) as LearningPathType[]) {
                dispatch(clearLearnings({ learningPathType }));
            }
        };
    }, [dispatch]);

    // Reset to the Learning tab when navigating via sidebar or breadcrumb
    useEffect(() => {
        if (location.state?.resetLearningTab) {
            onSectionTabClick(DEFAULT_TAB_INDEX);
        }
    }, [location.key]);

    useEffect(() => {
        void dispatch(fetchServerTime());
    }, [dispatch]);

    return (
        <LearningPageWrapper>
            <WelcomeSection />
            <div id={LEARNING_TAB_BAR_SECTION_ID}>
                <TabBar tabs={TAB_BAR_OPTIONS} onTabClick={onSectionTabClick} selectedTabIndex={selectedTabSection} />
                {TAB_BAR_ITEMS[selectedTabSection]}
            </div>
        </LearningPageWrapper>
    );
};

export default MyLearning;
